export { hasUserPermission } from './user-permissions.js';
export {
  stockTrialPreviewSchema,
  stockTrialHistorySchema,
  stockTrialQuerySchema,
} from './stock-trial.js';
export type { StockTrialPreview, StockTrialHistory } from './stock-trial.js';
export { inventoryColorSku, inventoryGroupCode, inventoryGroupKey } from './inventory-group.js';
import { z } from 'zod';

export const channelSchema = z.enum(['TIENDANUBE', 'MERCADOLIBRE']);
export type Channel = z.infer<typeof channelSchema>;

export const productPublicationPolicySchema = z.enum([
  'BOTH',
  'TIENDANUBE',
  'MERCADOLIBRE',
  'NONE',
]);
export type ProductPublicationPolicy = z.infer<typeof productPublicationPolicySchema>;

export const inventoryLocationPurposeSchema = z.enum(['GENERAL', 'WHOLESALE', 'RESERVE']);
export type InventoryLocationPurpose = z.infer<typeof inventoryLocationPurposeSchema>;

export const userPermissionSchema = z.enum([
  'dashboard.read',
  'dashboard.administration.read',
  'sales.read',
  'sales.write',
  'reports.sales.read',
  'inventory.read',
  'inventory.product.create',
  'inventory.product.data',
  'inventory.status',
  'inventory.wholesale',
  'inventory.total',
  'inventory.adjust',
  'inventory.trial.read',
  'inventory.adjust.status',
]);
export type UserPermission = z.infer<typeof userPermissionSchema>;
export const updateUserPermissionsSchema = z
  .object({
    permissions: z.array(userPermissionSchema).max(userPermissionSchema.options.length),
  })
  .strict()
  .superRefine((value, context) => {
    for (const [write, read] of [
      ['dashboard.administration.read', 'dashboard.read'],
      ['sales.write', 'sales.read'],
      ['inventory.adjust', 'inventory.read'],
      ['inventory.product.create', 'inventory.read'],
      ['inventory.product.data', 'inventory.read'],
      ['inventory.status', 'inventory.read'],
      ['inventory.wholesale', 'inventory.read'],
      ['inventory.total', 'inventory.read'],
      ['inventory.trial.read', 'inventory.read'],
      ['inventory.adjust.status', 'inventory.adjust'],
    ] as const) {
      if (value.permissions.includes(write) && !value.permissions.includes(read))
        context.addIssue({
          code: 'custom',
          path: ['permissions'],
          message: 'Para operar un módulo también debés habilitar su consulta.',
        });
    }
    if (new Set(value.permissions).size !== value.permissions.length)
      context.addIssue({
        code: 'custom',
        path: ['permissions'],
        message: 'Hay permisos repetidos.',
      });
  });
export type UpdateUserPermissions = z.infer<typeof updateUserPermissionsSchema>;

export const userRoleSchema = z.enum(['ADMIN', 'OPERATOR', 'ML_INTEGRATOR']);
export type UserRole = z.infer<typeof userRoleSchema>;

export const minimumInitialPasswordLength = 8;
export const maximumPasswordLength = 1_024;

export const organizationStateSchema = z.enum(['SETUP', 'READ_ONLY', 'TRIAL', 'ACTIVE']);
export type OrganizationState = z.infer<typeof organizationStateSchema>;

export const arcaInvoicingSettingsSchema = z
  .object({
    paused: z.boolean(),
    monthlyLimit: z.number().nonnegative().nullable(),
    monthlyInvoiced: z.number().nonnegative(),
  })
  .strict();
export type ArcaInvoicingSettings = z.infer<typeof arcaInvoicingSettingsSchema>;

export const updateArcaInvoicingSettingsSchema = z
  .object({
    paused: z.boolean(),
    monthlyLimit: z.number().finite().min(0).max(9_999_999_999_999.99).nullable(),
  })
  .strict();
export type UpdateArcaInvoicingSettings = z.infer<typeof updateArcaInvoicingSettingsSchema>;

export const inventoryMovementTypeSchema = z.enum([
  'INITIAL_IMPORT',
  'SALE',
  'CANCELLATION',
  'RETURN',
  'MANUAL_ADD',
  'MANUAL_SUBTRACT',
  'MANUAL_SET',
  'CORRECTION',
  'RECONCILIATION',
  'PURCHASE_RECEIPT',
  'TRANSFER_OUT',
  'TRANSFER_IN',
  'PRODUCTION_RECEIPT',
]);
export type InventoryMovementType = z.infer<typeof inventoryMovementTypeSchema>;

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    requestId: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});

export type ApiError = z.infer<typeof apiErrorSchema>;

export const paginationQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
  })
  .strict();

export const paginatedSchema = <T extends z.ZodType>(item: T) =>
  z.object({
    data: z.array(item),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
  });

// The legacy API/database field `email` is the unique login identifier. It accepts
// an email address or a username; existing clients and email logins remain compatible.
export const loginIdentifierSchema = z
  .string({ error: 'Ingresá un email o nombre de usuario.' })
  .trim()
  .toLowerCase()
  .min(1, 'Ingresá un email o nombre de usuario.')
  .max(320, 'El acceso no puede superar los 320 caracteres.')
  .refine(
    (value) => z.email().safeParse(value).success || /^[a-z0-9][a-z0-9._-]{2,63}$/.test(value),
    'Ingresá un email válido o un usuario de 3 a 64 caracteres, con letras, números, puntos, guiones o guiones bajos.',
  );

export const loginBodySchema = z
  .object({
    email: loginIdentifierSchema,
    password: z
      .string({ error: 'Ingresá tu contraseña.' })
      .min(1, 'Ingresá tu contraseña.')
      .max(
        maximumPasswordLength,
        `La contraseña no puede superar los ${maximumPasswordLength} caracteres.`,
      ),
  })
  .strict();

export type LoginBody = z.infer<typeof loginBodySchema>;

export const userSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  email: loginIdentifierSchema,
  displayName: z.string(),
  role: userRoleSchema,
  permissions: z.array(userPermissionSchema).nullable().optional(),
});
export type User = z.infer<typeof userSchema>;

export const employeeSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  position: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export const employeeInputSchema = z
  .object({
    name: z.string().trim().min(2).max(160),
    position: z.string().trim().max(120).nullable().optional(),
    email: z.string().trim().email().max(320).nullable().optional(),
    phone: z.string().trim().max(80).nullable().optional(),
    isActive: z.boolean().default(true),
  })
  .strict();
export const employeesListSchema = z.array(employeeSchema);
export type Employee = z.infer<typeof employeeSchema>;
export type EmployeeInput = z.infer<typeof employeeInputSchema>;

export const authSessionSchema = z.object({
  user: userSchema,
  organization: z.object({
    id: z.string().uuid(),
    name: z.string(),
    state: organizationStateSchema,
  }),
  csrfToken: z.string().min(32),
});
export type AuthSession = z.infer<typeof authSessionSchema>;

export const dashboardSchema = z.object({
  metrics: z.object({
    totalProducts: z.number().int().nonnegative(),
    synchronizedProducts: z.number().int().nonnegative(),
    productsWithDifferences: z.number().int().nonnegative(),
    pendingErrors: z.number().int().nonnegative(),
  }),
  organizationState: organizationStateSchema,
  connections: z.array(
    z.object({
      channel: channelSchema,
      status: z.enum(['CONNECTED', 'DISCONNECTED', 'ERROR', 'REAUTH_REQUIRED']),
      accountName: z.string().nullable(),
      lastCommunicationAt: z.string().datetime().nullable(),
      lastError: z.string().nullable(),
    }),
  ),
  lastSynchronizationAt: z.string().datetime().nullable(),
  business: z
    .object({
      customers: z.number().int().nonnegative().optional(),
      wholesaleCustomers: z.number().int().nonnegative().optional(),
      pendingOrders: z.number().int().nonnegative().optional(),
      preparingOrders: z.number().int().nonnegative().optional(),
      unbilledOrders: z.number().int().nonnegative().optional(),
      receivables: z.number().nonnegative().optional(),
      salesToday: z.number().nonnegative(),
      salesMonth: z.number().nonnegative(),
      invoicedMonth: z.number().nonnegative().optional(),
      collectedMonth: z.number().nonnegative().optional(),
      expensesMonth: z.number().nonnegative().optional(),
      purchasesMonth: z.number().nonnegative().optional(),
      wholesaleStock: z.number().int(),
      lowStockVariants: z.number().int().nonnegative(),
      productsWithStock: z.number().int().nonnegative(),
      variantsWithStock: z.number().int().nonnegative(),
      onlineStock: z.number().int(),
    })
    .optional(),
  activity: z.array(
    z.object({
      id: z.string().uuid(),
      type: inventoryMovementTypeSchema,
      description: z.string(),
      createdAt: z.string().datetime(),
    }),
  ),
});
export type Dashboard = z.infer<typeof dashboardSchema>;

export const inventoryFilterSchema = z.object({
  search: z.string().trim().max(120).optional(),
  status: z
    .enum([
      'SYNCED',
      'SYNC_PENDING',
      'SYNCING',
      'SYNC_RETRYING',
      'DIFFERENCE',
      'ERROR',
      'UNLINKED',
      'OUT_OF_STOCK',
    ])
    .optional(),
  color: z.string().trim().max(80).optional(),
  size: z.string().trim().max(40).optional(),
  stock: z.enum(['IN_STOCK', 'OUT_OF_STOCK']).optional(),
  pilot: z.enum(['INCLUDED', 'EXCLUDED', 'ALL']).optional(),
});
export type InventoryFilter = z.infer<typeof inventoryFilterSchema>;

export const inventoryQuerySchema = paginationQuerySchema.extend({
  ...inventoryFilterSchema.shape,
  view: z.enum(['VARIANTS', 'PRODUCTS']).optional(),
  sort: z.enum(['DEFAULT', 'STATUS']).optional(),
  productId: z.string().uuid().optional(),
  groupCode: z.string().trim().min(1).max(120).optional(),
  publication: z.enum(['EXCLUDE_PAUSED', 'PAUSED', 'ALL']).optional(),
});
export type InventoryQuery = z.infer<typeof inventoryQuerySchema>;

export const inventoryExportQuerySchema = inventoryFilterSchema.strict();
export type InventoryExportQuery = z.infer<typeof inventoryExportQuerySchema>;

export const inventoryValuationQuerySchema = paginationQuerySchema.extend({
  ...inventoryFilterSchema.shape,
  stockPurpose: z.enum(['ONLINE', 'WHOLESALE']).optional(),
});
export type InventoryValuationQuery = z.infer<typeof inventoryValuationQuerySchema>;

export const channelPriceEntrySchema = z.object({
  externalPriceId: z.string().nullable(),
  type: z.enum(['STANDARD', 'PROMOTION', 'QUANTITY', 'OTHER']),
  amount: z.number().nonnegative(),
  regularAmount: z.number().nonnegative().nullable(),
  currency: z.string(),
  context: z.array(z.string()),
  minQuantity: z.number().int().positive().nullable(),
  startsAt: z.string().datetime().nullable(),
  endsAt: z.string().datetime().nullable(),
});
export type ChannelPriceEntry = z.infer<typeof channelPriceEntrySchema>;

export const channelPriceBreakdownSchema = z.object({
  current: z.number().nonnegative().nullable(),
  regular: z.number().nonnegative().nullable(),
  promotional: z.number().nonnegative().nullable(),
  cost: z.number().nonnegative().nullable(),
  currency: z.string().nullable(),
  externalPriceId: z.string().nullable(),
  entries: z.array(channelPriceEntrySchema),
});
export type ChannelPriceBreakdown = z.infer<typeof channelPriceBreakdownSchema>;

export const inventoryPublicationAccountSchema = z.object({
  connectionId: z.string().min(1),
  channel: channelSchema,
  label: z.string().min(1),
  activeVariants: z.number().int().nonnegative(),
  paused: z.boolean(),
  inactive: z.boolean(),
  syncedVariants: z.number().int().nonnegative(),
  missingVariants: z.number().int().nonnegative().optional(),
  totalVariants: z.number().int().positive(),
});
export type InventoryPublicationAccount = z.infer<typeof inventoryPublicationAccountSchema>;

export const inventoryRowSchema = z.object({
  variantId: z.string().uuid(),
  productId: z.string().uuid(),
  groupCode: z.string().nullable().optional(),
  groupName: z.string().optional(),
  colorSku: z.string().nullable().optional(),
  syncAwaitingConfirmation: z.boolean().optional(),
  inSyncScope: z.boolean().nullable().optional(),
  // Complete product-group state, calculated before color/size/stock filters.
  pausedPublicationChannels: z.array(z.enum(['TIENDANUBE', 'MERCADOLIBRE'])).optional(),
  activeChannels: z.array(channelSchema).optional(),
  groupActiveChannels: z.array(channelSchema).optional(),
  publicationAccounts: z.array(inventoryPublicationAccountSchema).optional(),
  groupPublicationAccounts: z.array(inventoryPublicationAccountSchema).optional(),
  colorPublicationAccounts: z.array(inventoryPublicationAccountSchema).optional(),
  // A channel is paused/inactive only when it has no active publication for this variant.
  pausedChannels: z.array(z.enum(['TIENDANUBE', 'MERCADOLIBRE'])).optional(),
  inactiveChannels: z.array(channelSchema).optional(),
  groupInactiveChannels: z.array(channelSchema).optional(),
  // Channels paused for every variant in the complete group, before inventory filters.
  groupPausedChannels: z.array(z.enum(['TIENDANUBE', 'MERCADOLIBRE'])).optional(),
  syncedChannels: z.array(z.enum(['TIENDANUBE', 'MERCADOLIBRE'])).optional(),
  groupSyncedChannels: z
    .array(
      z.object({
        channel: z.enum(['TIENDANUBE', 'MERCADOLIBRE']),
        syncedVariants: z.number().int().positive(),
        totalVariants: z.number().int().positive(),
      }),
    )
    .optional(),
  sku: z.string().nullable(),
  productName: z.string(),
  variantName: z.string().nullable(),
  color: z.string().nullable(),
  size: z.string().nullable(),
  imageUrl: z.string().url().nullable(),
  availableStock: z.number().int(),
  tiendanubeStock: z.number().int().nullable(),
  mercadolibreStock: z.number().int().nullable(),
  mercadolibre1Stock: z.number().int().nullable(),
  mercadolibre2Stock: z.number().int().nullable(),
  wholesaleStock: z.number().int(),
  safetyStock: z.number().int().nonnegative(),
  unitPrice: z.number().nonnegative().nullable(),
  tiendanubePrice: channelPriceBreakdownSchema.nullable(),
  mercadolibrePrice: channelPriceBreakdownSchema.nullable(),
  status: z.enum([
    'SYNCED',
    'SYNC_PENDING',
    'SYNCING',
    'SYNC_RETRYING',
    'DIFFERENCE',
    'ERROR',
    'UNLINKED',
    'OUT_OF_STOCK',
  ]),
});
export type InventoryRow = z.infer<typeof inventoryRowSchema>;

export const inventoryListSchema = paginatedSchema(inventoryRowSchema).extend({
  facets: z
    .object({
      colors: z.array(z.string()),
      sizes: z.array(z.string()),
    })
    .optional(),
});
export type InventoryList = z.infer<typeof inventoryListSchema>;

export const catalogSyncProgressSchema = z
  .object({
    stage: z.enum(['READ_PRODUCT', 'READ_VARIANTS', 'READ_STOCK', 'SAVE', 'FINALIZE']),
    completed: z.number().int().nonnegative(),
    total: z.number().int().nonnegative().nullable(),
    updatedAt: z.string().datetime(),
  })
  .refine((progress) => progress.total === null || progress.completed <= progress.total, {
    message: 'Completed catalog work cannot exceed its total',
  });
export type CatalogSyncProgress = z.infer<typeof catalogSyncProgressSchema>;

const inventorySyncJobSchema = z.object({
  id: z.string().uuid(),
  startedAt: z.string().datetime().nullable(),
  heartbeatAt: z.string().datetime().nullable(),
  attempts: z.number().int().nonnegative(),
  retryAt: z.string().datetime().nullable(),
  progress: catalogSyncProgressSchema.optional(),
});

export const inventorySyncProgressSchema = z.object({
  telemetry: z
    .object({
      checkedAt: z.string().datetime(),
      workerLastSeenAt: z.string().datetime().nullable(),
      lastCompletedAt: z.string().datetime().nullable(),
    })
    .optional(),
  activity: z
    .array(
      z.object({
        id: z.string().min(1),
        variantId: z.string().uuid(),
        productName: z.string(),
        sku: z.string().min(1),
        color: z.string().nullable(),
        size: z.string().nullable(),
        imageUrl: z.string().url().nullable(),
        channel: channelSchema,
        direction: z.enum(['SEND', 'RECEIVE', 'VERIFY']),
        job: inventorySyncJobSchema.optional(),
        waitingReason: z.string().nullable().optional(),
        confirmation: z
          .object({
            completed: z.number().int().nonnegative(),
            total: z.number().int().nonnegative(),
          })
          .optional(),
        status: z.enum(['PENDING', 'RUNNING', 'RETRYING']),
        queuedAt: z.string().datetime(),
        retryReason: z.string().nullable(),
      }),
    )
    .optional(),
  overall: z
    .object({
      createdAt: z.string().datetime(),
      total: z.number().int().positive(),
      completed: z.number().int().nonnegative(),
      confirmedDestinations: z.number().int().nonnegative(),
      totalDestinations: z.number().int().nonnegative(),
      pending: z.number().int().nonnegative(),
      running: z.number().int().nonnegative(),
      retrying: z.number().int().nonnegative(),
      failed: z.number().int().nonnegative(),
      percentage: z.number().int().min(0).max(100),
    })
    .nullable(),
  latestAdjustment: z
    .object({
      requestId: z.string().min(1).max(64),
      createdAt: z.string().datetime(),
      total: z.number().int().positive(),
      completed: z.number().int().nonnegative(),
      pending: z.number().int().nonnegative(),
      running: z.number().int().nonnegative(),
      retrying: z.number().int().nonnegative(),
      failed: z.number().int().nonnegative(),
      percentage: z.number().int().min(0).max(100),
    })
    .nullable(),
  discovery: z.object({
    pending: z.number().int().nonnegative(),
    running: z.number().int().nonnegative(),
    retrying: z.number().int().nonnegative(),
    items: z
      .array(
        z.object({
          id: z.string().uuid(),
          type: z.enum(['CATALOG_IMPORT', 'CHANNEL_SYNC_REQUESTED', 'PRODUCT_STOCK_BOOTSTRAP']),
          channel: channelSchema.nullable(),
          productName: z.string().nullable(),
          reference: z.string().nullable(),
          publicationKey: z.string().optional(),
          imageUrl: z.string().url().nullable(),
          description: z.string(),
          job: inventorySyncJobSchema.optional(),
          queuedAt: z.string().datetime(),
          status: z.enum(['PENDING', 'RUNNING', 'RETRYING']),
          retryAt: z.string().datetime().nullable(),
          retryReason: z.string().nullable(),
        }),
      )
      .optional(),
  }),
  queue: z.object({
    pending: z.number().int().nonnegative(),
    running: z.number().int().nonnegative(),
    retrying: z.number().int().nonnegative(),
    items: z.array(
      z.object({
        id: z.string().uuid(),
        variantId: z.string().uuid().nullable(),
        productName: z.string(),
        sku: z.string().nullable(),
        color: z.string().nullable(),
        size: z.string().nullable(),
        imageUrl: z.string().url().nullable(),
        queuedAt: z.string().datetime(),
        status: z.enum(['PENDING', 'RUNNING', 'RETRYING']),
        retryAt: z.string().datetime().nullable(),
        retryReason: z.string().nullable(),
        waitingReason: z.string().nullable(),
        job: inventorySyncJobSchema.optional(),
        publicationProgress: z.object({
          completed: z.number().int().nonnegative(),
          total: z.number().int().nonnegative(),
          percentage: z.number().int().min(0).max(100).nullable(),
        }),
      }),
    ),
  }),
});
export type InventorySyncProgress = z.infer<typeof inventorySyncProgressSchema>;

export const inventoryValuationRowSchema = inventoryRowSchema.extend({
  onlineStock: z.number().int(),
  valuation: z.number().nonnegative().nullable(),
});
export const inventoryValuationSchema = paginatedSchema(inventoryValuationRowSchema).extend({
  facets: z.object({
    colors: z.array(z.string()),
    sizes: z.array(z.string()),
  }),
  summary: z.object({
    units: z.number().int(),
    valuedUnits: z.number().int().nonnegative(),
    unvaluedUnits: z.number().int().nonnegative(),
    totalValue: z.number().nonnegative(),
    currency: z.literal('ARS'),
  }),
});
export type InventoryValuation = z.infer<typeof inventoryValuationSchema>;

export const adjustmentBodySchema = z
  .object({
    productVariantId: z.string().uuid('La variante seleccionada no es válida.'),
    inventoryLocationId: z.string().uuid('Seleccioná una ubicación de inventario válida.'),
    operation: z.enum(['ADD', 'SUBTRACT', 'SET'], {
      error: 'Seleccioná una operación de ajuste válida.',
    }),
    quantity: z
      .number({ error: 'Ingresá una cantidad válida.' })
      .int('La cantidad debe ser un número entero.')
      .min(0, 'La cantidad no puede ser negativa.')
      .max(1_000_000, 'La cantidad no puede superar 1.000.000 unidades.'),
    reason: z
      .string({ error: 'Ingresá el motivo del ajuste.' })
      .trim()
      .min(3, 'El motivo debe tener al menos 3 caracteres.')
      .max(500, 'El motivo no puede superar 500 caracteres.'),
    stockPurpose: z.enum(['ONLINE', 'WHOLESALE']).default('ONLINE'),
    idempotencyKey: z.string().uuid('La solicitud de ajuste no es válida.'),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.operation !== 'SET' && value.quantity === 0) {
      context.addIssue({
        code: 'custom',
        path: ['quantity'],
        message: 'Ingresá una cantidad mayor a cero.',
      });
    }
  });
export type AdjustmentBody = z.infer<typeof adjustmentBodySchema>;

export const bulkAdjustmentBodySchema = z
  .object({
    items: z
      .array(
        z.object({
          productVariantId: z.string().uuid('La variante seleccionada no es válida.'),
          quantity: z
            .number({ error: 'Ingresá una cantidad válida.' })
            .int('La cantidad debe ser un número entero.')
            .min(0, 'La cantidad no puede ser negativa.')
            .max(1_000_000, 'La cantidad no puede superar 1.000.000 unidades.'),
          idempotencyKey: z.string().uuid('La solicitud de ajuste no es válida.'),
        }),
      )
      .min(1, 'Seleccioná al menos una variante.')
      .max(1_000, 'No se pueden ajustar más de 1.000 variantes por operación.'),
    operation: z.enum(['ADD', 'SUBTRACT', 'SET'], {
      error: 'Seleccioná una operación de ajuste válida.',
    }),
    reason: z
      .string({ error: 'Ingresá el motivo del ajuste.' })
      .trim()
      .min(3, 'El motivo debe tener al menos 3 caracteres.')
      .max(500, 'El motivo no puede superar 500 caracteres.'),
    inventoryLocationId: z.string().uuid('Seleccioná una ubicación de inventario válida.'),
    stockPurpose: z.enum(['ONLINE', 'WHOLESALE']).default('ONLINE'),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.operation !== 'SET') {
      value.items.forEach((item, index) => {
        if (item.quantity === 0) {
          context.addIssue({
            code: 'custom',
            path: ['items', index, 'quantity'],
            message: 'Ingresá una cantidad mayor a cero.',
          });
        }
      });
    }
  });
export type BulkAdjustmentBody = z.infer<typeof bulkAdjustmentBodySchema>;

export const adjustmentResponseSchema = z.object({
  movementId: z.string().uuid(),
  previousStock: z.number().int(),
  delta: z.number().int(),
  resultingStock: z.number().int().nonnegative(),
  alreadyApplied: z.boolean(),
});
export const bulkAdjustmentResponseSchema = z.object({
  results: z.array(adjustmentResponseSchema),
  appliedCount: z.number().int().nonnegative(),
  alreadyAppliedCount: z.number().int().nonnegative(),
});
export type BulkAdjustmentResponse = z.infer<typeof bulkAdjustmentResponseSchema>;

export const movementsQuerySchema = paginationQuerySchema.extend({
  productId: z.string().uuid().optional(),
  groupCode: z.string().trim().min(1).max(120).optional(),
  productVariantId: z.string().uuid().optional(),
  type: inventoryMovementTypeSchema.optional(),
  search: z.string().trim().max(120).optional(),
  channel: channelSchema.optional(),
  locationPurpose: inventoryLocationPurposeSchema.optional(),
  color: z.string().trim().max(120).optional(),
  size: z.string().trim().max(120).optional(),
});
export type MovementsQuery = z.infer<typeof movementsQuerySchema>;

export const productAnalyticsQuerySchema = movementsQuerySchema
  .pick({ productId: true, groupCode: true, channel: true, color: true, size: true })
  .extend({ from: z.string().date(), to: z.string().date() })
  .strict()
  .superRefine((value, context) => {
    if (!value.productId && !value.groupCode)
      context.addIssue({ code: 'custom', message: 'Indicá un producto o grupo.' });
    const days = (Date.parse(value.to) - Date.parse(value.from)) / 86_400_000;
    if (days < 0 || days >= 366)
      context.addIssue({
        code: 'custom',
        path: ['to'],
        message: 'Elegí un período de hasta 366 días, con la fecha final posterior al inicio.',
      });
  });
export type ProductAnalyticsQuery = z.infer<typeof productAnalyticsQuerySchema>;

const productAnalyticsMetricSchema = z.object({
  soldUnits: z.number().int().nonnegative(),
  returnedUnits: z.number().int().nonnegative(),
  cancelledUnits: z.number().int().nonnegative(),
  saleMovements: z.number().int().nonnegative(),
});
export const productAnalyticsSchema = z.object({
  from: z.string().date(),
  to: z.string().date(),
  generatedAt: z.string().datetime(),
  summary: productAnalyticsMetricSchema.extend({
    netSoldUnits: z.number().int(),
    availableStock: z.number().int(),
    stockoutVariants: z.number().int().nonnegative(),
    variantsWithStock: z.number().int().nonnegative(),
    todaySoldUnits: z.number().int().nonnegative(),
    dailyAverage: z.number().nonnegative(),
    coverageDays: z.number().nonnegative().nullable(),
  }),
  byDay: z.array(productAnalyticsMetricSchema.extend({ date: z.string().date() })),
  byChannel: z.array(productAnalyticsMetricSchema.extend({ channel: channelSchema.nullable() })),
  byVariant: z.array(
    productAnalyticsMetricSchema.extend({
      variantId: z.string().uuid(),
      sku: z.string().nullable(),
      name: z.string().nullable(),
      color: z.string().nullable(),
      size: z.string().nullable(),
      availableStock: z.number().int(),
    }),
  ),
});
export type ProductAnalytics = z.infer<typeof productAnalyticsSchema>;

export const stockMovementSchema = z.object({
  id: z.string().uuid(),
  productVariantId: z.string().uuid(),
  inventoryLocationId: z.string().uuid(),
  sku: z.string().nullable(),
  productName: z.string().nullable(),
  type: inventoryMovementTypeSchema,
  previousStock: z.number().int(),
  delta: z.number().int(),
  resultingStock: z.number().int(),
  reason: z.string().nullable(),
  source: z.enum(['INTERNAL', 'TIENDANUBE', 'MERCADOLIBRE', 'RECONCILIATION']),
  channel: channelSchema.nullable(),
  locationPurpose: inventoryLocationPurposeSchema,
  externalReference: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export const stockMovementsListSchema = paginatedSchema(stockMovementSchema);
export type StockMovementsList = z.infer<typeof stockMovementsListSchema>;

export const productListQuerySchema = inventoryQuerySchema;
export const productMediaSchema = z.object({
  images: z.array(
    z.object({
      id: z.string(),
      url: z.string().url(),
      position: z.number().int().nonnegative().nullable(),
    }),
  ),
  videos: z.array(
    z.object({
      id: z.string(),
      url: z.string().url(),
      provider: z.string().nullable(),
    }),
  ),
});
export const mediaUploadResponseSchema = z
  .object({
    url: z.string().url(),
    mimeType: z.enum([
      'image/jpeg',
      'image/png',
      'image/webp',
      'video/mp4',
      'video/webm',
      'video/ogg',
    ]),
    size: z.number().int().positive(),
  })
  .strict();
export type MediaUploadResponse = z.infer<typeof mediaUploadResponseSchema>;
const productAttributeSchema = z.object({
  id: z.string().trim().max(120).default(''),
  name: z.string().trim().max(160).default(''),
  valueId: z.string().trim().max(191).nullable().default(null),
  valueName: z.string().trim().max(500).default(''),
});

const productChannelOverridesSchema = z
  .object({
    brand: z.string().trim().max(255).nullable().optional(),
    categoryId: z.string().trim().max(191).nullable().optional(),
    tiendanubeCategoryIds: z.array(z.string().trim().max(191)).max(100).optional(),
    listingTypeId: z.string().trim().max(80).optional(),
    imageUrls: z.array(z.string().url()).max(250).optional(),
    videoUrl: z.string().url().nullable().optional(),
    attributes: z.array(productAttributeSchema).max(100).optional(),
    tags: z.string().max(500).optional(),
    freeShipping: z.boolean().optional(),
    seoTitle: z.string().max(70).optional(),
    seoDescription: z.string().max(320).optional(),
  })
  .strict();

export const productCatalogDataSchema = z
  .object({
    // Set by the server for new active products until their first shared stock snapshot is verified.
    automaticStockInitializationPending: z.boolean().optional(),
    automaticStockInitializationSource: z
      .enum(['PLATFORMS', 'SHARED_PLATFORMS', 'SYSTEM'])
      .optional(),
    automaticStockInitializedVariantIds: z.array(z.string().uuid()).optional(),
    automaticStockDiscoveredVariantIds: z.array(z.string().uuid()).optional(),
    // Reviewed internal color identity; platform labels may differ.
    inventoryColor: z
      .object({
        name: z.string().trim().min(1).max(160),
        sku: z.string().trim().min(1).max(120),
      })
      .optional(),
    channelNames: z
      .object({
        TIENDANUBE: z.string().trim().max(500).default(''),
        MERCADOLIBRE: z.string().trim().max(500).default(''),
      })
      .default({ TIENDANUBE: '', MERCADOLIBRE: '' }),
    description: z.string().max(100_000).default(''),
    channelDescriptions: z
      .object({
        TIENDANUBE: z.string().max(100_000).default(''),
        MERCADOLIBRE: z.string().max(100_000).default(''),
      })
      .default({ TIENDANUBE: '', MERCADOLIBRE: '' }),
    channelOverrides: z
      .object({
        TIENDANUBE: productChannelOverridesSchema.default({}),
        MERCADOLIBRE: productChannelOverridesSchema.default({}),
      })
      .default({ TIENDANUBE: {}, MERCADOLIBRE: {} }),
    brand: z.string().trim().max(255).nullable().default(null),
    categoryId: z.string().trim().max(191).nullable().default(null),
    tiendanubeCategoryIds: z.array(z.string().trim().max(191)).max(100).default([]),
    currency: z.string().trim().min(3).max(10).default('ARS'),
    buyingMode: z.enum(['buy_it_now', 'classified']).default('buy_it_now'),
    listingTypeId: z.string().trim().max(80).default('gold_special'),
    condition: z.enum(['new', 'used', 'not_specified']).default('new'),
    imageUrls: z.array(z.string().url()).max(250).default([]),
    channelImageUrls: z
      .object({
        TIENDANUBE: z.array(z.string().url()).max(250).default([]),
        MERCADOLIBRE: z.array(z.string().url()).max(250).default([]),
      })
      .default({ TIENDANUBE: [], MERCADOLIBRE: [] }),
    videoUrl: z.string().url().nullable().default(null),
    attributes: z.array(productAttributeSchema).max(100).default([]),
    tags: z.string().max(500).default(''),
    freeShipping: z.boolean().default(false),
    visibility: z.enum(['visible', 'unlisted', 'hidden']).default('visible'),
    seoTitle: z.string().max(70).default(''),
    seoDescription: z.string().max(320).default(''),
  })
  .strict();
export type ProductCatalogData = z.infer<typeof productCatalogDataSchema>;

export const productVariantCatalogDataSchema = z
  .object({
    // A discovered variation may have a different color from its reviewed parent.
    inventoryColor: z
      .object({
        name: z.string().trim().min(1).max(160),
        sku: z.string().trim().min(1).max(120).nullable(),
      })
      .optional(),
    values: z
      .array(z.object({ name: z.string().trim().max(160), value: z.string().trim().max(255) }))
      .max(3)
      .default([]),
    price: z.number().nonnegative().default(0),
    promotionalPrice: z.number().nonnegative().nullable().default(null),
    wholesalePrice: z.number().nonnegative().default(0),
    channelPrices: z
      .object({
        TIENDANUBE: z
          .object({
            price: z.number().nonnegative().nullable().default(null),
            promotionalPrice: z.number().nonnegative().nullable().default(null),
          })
          .default({ price: null, promotionalPrice: null }),
        MERCADOLIBRE: z
          .object({
            price: z.number().nonnegative().nullable().default(null),
            promotionalPrice: z.number().nonnegative().nullable().default(null),
          })
          .default({ price: null, promotionalPrice: null }),
      })
      .default({
        TIENDANUBE: { price: null, promotionalPrice: null },
        MERCADOLIBRE: { price: null, promotionalPrice: null },
      }),
    channelImageUrls: z
      .object({
        TIENDANUBE: z.array(z.string().url()).max(250).default([]),
        MERCADOLIBRE: z.array(z.string().url()).max(250).default([]),
      })
      .default({ TIENDANUBE: [], MERCADOLIBRE: [] }),
    stock: z.number().int().nonnegative().default(0),
    // Optional to keep compatibility with catalog data created before stock pools
    // were exposed in the product editor.
    onlineStock: z.number().int().nonnegative().optional(),
    wholesaleStock: z.number().int().nonnegative().optional(),
    weight: z.number().nonnegative().nullable().default(null),
    width: z.number().nonnegative().nullable().default(null),
    height: z.number().nonnegative().nullable().default(null),
    depth: z.number().nonnegative().nullable().default(null),
    barcode: z.string().trim().max(80).nullable().default(null),
  })
  .strict();
export type ProductVariantCatalogData = z.infer<typeof productVariantCatalogDataSchema>;

export const productEditorSchema = z
  .object({
    name: z.string().trim().min(1).max(500),
    initialStockSource: z.enum(['PLATFORMS', 'SYSTEM']).optional(),
    groupCode: z.string().trim().max(120).nullable().optional(),
    publicationPolicy: productPublicationPolicySchema,
    data: productCatalogDataSchema,
    variants: z
      .array(
        z.object({
          id: z.string().uuid().optional(),
          sku: z.string().trim().max(191).nullable().default(null),
          name: z.string().trim().max(500).nullable().default(null),
          safetyStock: z.number().int().nonnegative().default(0),
          data: productVariantCatalogDataSchema,
        }),
      )
      .min(1)
      .max(1_000),
  })
  .strict();
export type ProductEditor = z.input<typeof productEditorSchema>;
export const productSaveResponseSchema = z.object({
  id: z.string().uuid(),
  publicationJobId: z.string().uuid().nullable(),
});
export const productVariantDetailSchema = z.object({
  id: z.string().uuid(),
  sku: z.string().nullable(),
  name: z.string().nullable(),
  safetyStock: z.number().int().nonnegative(),
  catalogData: productVariantCatalogDataSchema,
  availableStock: z.number().int(),
  locations: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      kind: z.string(),
      purpose: z.enum(['GENERAL', 'WHOLESALE', 'RESERVE']),
      quantity: z.number().int(),
      includedInAvailable: z.boolean(),
    }),
  ),
  listings: z.array(
    z.object({
      id: z.string().uuid(),
      channelConnectionId: z.string().uuid().optional(),
      channel: channelSchema,
      externalListingId: z.string(),
      externalVariantId: z.string().nullable(),
      externalSku: z.string().nullable(),
      publishedStock: z.number().int().nullable(),
      observedStock: z.number().int().nullable(),
      externalUrl: z.string().url().nullable(),
      status: z.string(),
      media: productMediaSchema,
    }),
  ),
  movements: z.array(
    z.object({
      id: z.string().uuid(),
      type: inventoryMovementTypeSchema,
      delta: z.number().int(),
      resultingStock: z.number().int(),
      reason: z.string().nullable(),
      createdAt: z.string().datetime(),
    }),
  ),
});

export const productDetailSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  publicationPolicy: productPublicationPolicySchema,
  data: productCatalogDataSchema,
  variants: z.array(productVariantDetailSchema),
});
export type ProductDetail = z.infer<typeof productDetailSchema>;

export const updateProductPublicationPolicySchema = z
  .object({ publicationPolicy: productPublicationPolicySchema })
  .strict();
export type UpdateProductPublicationPolicy = z.infer<typeof updateProductPublicationPolicySchema>;

export const meliStockTargetBodySchema = z.discriminatedUnion('kind', [
  z
    .object({
      // This is the seller-managed location documented by Mercado Libre; Full
      // (`meli_facility`) is deliberately absent and can never be configured.
      kind: z.literal('SELLING_ADDRESS'),
    })
    .strict(),
  z
    .object({
      kind: z.literal('SELLER_WAREHOUSE'),
      storeId: z.string().trim().min(1).max(191),
      networkNodeId: z.string().trim().min(1).max(191),
    })
    .strict(),
]);
export type MeliStockTargetBody = z.infer<typeof meliStockTargetBodySchema>;

export const matchingRowSchema = z.object({
  listingId: z.string().uuid(),
  channel: channelSchema,
  externalListingId: z.string(),
  externalVariantId: z.string().nullable(),
  externalUserProductId: z.string().nullable(),
  sku: z.string().nullable(),
  title: z.string(),
  status: z.enum(['LINKED', 'UNLINKED', 'AMBIGUOUS', 'DUPLICATE_SKU', 'MISSING_SKU']),
  candidateVariantIds: z.array(z.string().uuid()),
  linkedVariantId: z.string().uuid().nullable(),
  stockPolicy: z.enum(['MANAGED', 'READ_ONLY', 'CHANNEL_MANAGED']),
  stockTarget: meliStockTargetBodySchema.nullable(),
});
export type MatchingRow = z.infer<typeof matchingRowSchema>;

export const matchingQuerySchema = paginationQuerySchema
  .extend({
    search: z.string().trim().max(120).optional(),
    status: matchingRowSchema.shape.status.optional(),
  })
  .strict();

export const matchingSummarySchema = z.object({
  linked: z.number().int().nonnegative(),
  unresolved: z.number().int().nonnegative(),
  missingSku: z.number().int().nonnegative(),
  unlinked: z.number().int().nonnegative(),
});

export const matchingListSchema = paginatedSchema(matchingRowSchema).extend({
  summary: matchingSummarySchema,
});
export type MatchingList = z.infer<typeof matchingListSchema>;

export const linkListingBodySchema = z
  .object({
    productVariantId: z.string().uuid(),
  })
  .strict();

export const integrationStatusSchema = z.object({
  id: z.string().uuid().nullable().default(null),
  channel: channelSchema,
  externalAccountId: z.string().nullable().default(null),
  status: z.enum(['CONNECTED', 'DISCONNECTED', 'ERROR', 'REAUTH_REQUIRED']),
  accountName: z.string().nullable(),
  lastCommunicationAt: z.string().datetime().nullable(),
  lastError: z.string().nullable(),
  capabilities: z.array(z.string()),
});
export type IntegrationStatus = z.infer<typeof integrationStatusSchema>;

export const integrationSyncBodySchema = z
  .object({ connectionId: z.string().uuid().optional() })
  .strict();
export type IntegrationSyncBody = z.infer<typeof integrationSyncBodySchema>;

/** A same-origin API response that tells the browser where to start OAuth. */
export const integrationAuthorizationSchema = z
  .object({
    authorizationUrl: z.string().url(),
  })
  .strict();
export type IntegrationAuthorization = z.infer<typeof integrationAuthorizationSchema>;

export const mercadoLibreCatalogOptionsSchema = z
  .object({
    listingTypes: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        remainingListings: z.number().int().nonnegative().nullable(),
      }),
    ),
    categories: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    ),
  })
  .strict();
export type MercadoLibreCatalogOptions = z.infer<typeof mercadoLibreCatalogOptionsSchema>;

export const tiendanubeCategoriesSchema = z
  .object({
    categories: z.array(
      z
        .object({
          id: z.string(),
          name: z.string(),
          parentId: z.string().nullable(),
        })
        .strict(),
    ),
  })
  .strict();
export type TiendanubeCategories = z.infer<typeof tiendanubeCategoriesSchema>;

export const syncJobSchema = z.object({
  id: z.string().uuid(),
  channel: channelSchema.nullable(),
  type: z.string(),
  status: z.enum(['PENDING', 'RUNNING', 'SUCCEEDED', 'RETRYING', 'DEAD_LETTER', 'CANCELLED']),
  attempts: z.number().int().nonnegative(),
  maxAttempts: z.number().int().positive(),
  lastError: z.string().nullable(),
  availableAt: z.string().datetime(),
  createdAt: z.string().datetime(),
});
export type SyncJob = z.infer<typeof syncJobSchema>;

export const createUserBodySchema = z
  .object({
    email: loginIdentifierSchema,
    displayName: z
      .string({ error: 'Ingresá el nombre visible del usuario.' })
      .trim()
      .min(1, 'Ingresá el nombre visible del usuario.')
      .max(120, 'El nombre visible no puede superar los 120 caracteres.'),
    password: z
      .string({ error: 'Ingresá una contraseña inicial.' })
      .min(
        minimumInitialPasswordLength,
        `La contraseña inicial debe tener al menos ${minimumInitialPasswordLength} caracteres.`,
      )
      .max(
        maximumPasswordLength,
        `La contraseña inicial no puede superar los ${maximumPasswordLength} caracteres.`,
      ),
    role: userRoleSchema,
  })
  .strict();
export type CreateUserBody = z.infer<typeof createUserBodySchema>;

export const reconcileBodySchema = z
  .object({
    productVariantId: z.string().uuid().optional(),
    channel: channelSchema.optional(),
  })
  .strict();
export type ReconcileBody = z.infer<typeof reconcileBodySchema>;

export const reconciliationHistoryQuerySchema = paginationQuerySchema.extend({
  channel: channelSchema.optional(),
  status: z.enum(['PENDING', 'RUNNING', 'COMPLETED', 'FAILED', 'CANCELLED']).optional(),
});
export const reconciliationRunSchema = z.object({
  id: z.string().uuid(),
  channel: channelSchema.nullable(),
  mode: z.enum(['INCREMENTAL', 'MANUAL', 'FULL']),
  status: z.enum(['PENDING', 'RUNNING', 'COMPLETED', 'FAILED', 'CANCELLED']),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  lastError: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export const reconciliationHistoryListSchema = paginatedSchema(reconciliationRunSchema);
export type ReconciliationHistoryList = z.infer<typeof reconciliationHistoryListSchema>;

export const idParamSchema = z.object({ id: z.string().uuid() }).strict();
export const productGroupParamSchema = z
  .object({ code: z.string().trim().min(1).max(191) })
  .strict();

export const orderDisplayStatusSchema = z.enum([
  'PAID',
  'PENDING',
  'CANCELLED',
  'REFUNDED',
  'OTHER',
]);
export type OrderDisplayStatus = z.infer<typeof orderDisplayStatusSchema>;

const orderDateQuerySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha debe tener formato YYYY-MM-DD.');

const queryBooleanSchema = z.preprocess(
  (value) => (value === 'true' ? true : value === 'false' ? false : value),
  z.boolean().optional(),
);

const operationalOrderStatusSchema = z.enum([
  'PENDING',
  'PREPARING',
  'FINISHED',
  'DISPATCHED',
  'DELIVERED',
  'CANCELLED',
]);
const orderChannelSchema = z.enum(['TIENDANUBE', 'MERCADOLIBRE', 'MANUAL']);

export const ordersQuerySchema = paginationQuerySchema.extend({
  orderId: z.string().uuid().optional(),
  search: z.string().trim().max(120).optional(),
  channel: orderChannelSchema.optional(),
  status: orderDisplayStatusSchema.optional(),
  customerId: z.string().uuid().optional(),
  operationalStatus: operationalOrderStatusSchema.optional(),
  from: orderDateQuerySchema.optional(),
  to: orderDateQuerySchema.optional(),
  productId: z.string().uuid().optional(),
  salesOnly: queryBooleanSchema,
  includeFilters: queryBooleanSchema,
});
export type OrdersQuery = z.infer<typeof ordersQuerySchema>;

export const orderLineViewSchema = z.object({
  id: z.string().uuid(),
  productVariantId: z.string().uuid().nullable(),
  productId: z.string().uuid().nullable(),
  sku: z.string().nullable(),
  productName: z.string().nullable(),
  variantName: z.string().nullable(),
  imageUrl: z.string().url().nullable(),
  externalSku: z.string().nullable(),
  externalListingId: z.string().nullable(),
  externalVariantId: z.string().nullable(),
  publicationUrl: z.string().url().nullable(),
  quantity: z.number().int().positive(),
  unitPrice: z.number().nullable().optional(),
  discountPercent: z.number().nullable().optional(),
  lineTotal: z.number().nullable().optional(),
});
export type OrderLineView = z.infer<typeof orderLineViewSchema>;

const orderInvoiceSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(['FISCAL', 'INTERNAL']),
  documentType: z.enum(['INVOICE', 'CREDIT_NOTE', 'DEBIT_NOTE']),
  voucherType: z.enum(['FACTURA_A', 'FACTURA_B', 'FACTURA_C', 'FACTURA_X']),
  status: z.enum(['DRAFT', 'ISSUED', 'CANCELLED', 'ERROR']),
  pointOfSale: z.string().nullable(),
  number: z.string().nullable(),
  customerName: z.string().nullable(),
  customerTaxId: z.string().nullable(),
  currency: z.string(),
  totalAmount: z.number(),
  issuedAt: z.string().datetime().nullable(),
  arcaStatus: z.enum(['NOT_APPLICABLE', 'PENDING', 'ISSUED', 'ERROR']),
  errorMessage: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type OrderInvoice = z.infer<typeof orderInvoiceSchema>;

const orderCustomerSchema = z.object({
  businessName: z.string(),
  contactName: z.string().nullable(),
  taxId: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  postalCode: z.string().nullable(),
});
export type OrderCustomer = z.infer<typeof orderCustomerSchema>;

export const orderRowSchema = z.object({
  id: z.string().uuid(),
  channel: z.enum(['TIENDANUBE', 'MERCADOLIBRE', 'MANUAL']),
  sourceChannel: channelSchema.nullable(),
  externalOrderId: z.string(),
  orderNumber: z.string().nullable(),
  state: z.string(),
  operationalStatus: z.enum([
    'PENDING',
    'PREPARING',
    'FINISHED',
    'DISPATCHED',
    'DELIVERED',
    'CANCELLED',
  ]),
  billingStatus: z.enum(['PENDING', 'PARTIAL', 'COMPLETE', 'INCOMPLETE', 'CANCELLED']),
  billingStatusReason: z.string().nullable(),
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  customer: orderCustomerSchema.nullable().optional(),
  sellerId: z.string().uuid().nullable(),
  sellerName: z.string().nullable(),
  status: orderDisplayStatusSchema,
  totalAmount: z.number().nullable(),
  currency: z.string().nullable(),
  orderedAt: z.string().datetime(),
  units: z.number().int().nonnegative(),
  invoice: orderInvoiceSchema.nullable().optional(),
  lines: z.array(orderLineViewSchema),
});
export type OrderRow = z.infer<typeof orderRowSchema>;

export const ordersListSchema = paginatedSchema(orderRowSchema).extend({
  summary: z.object({
    amount: z.number(),
    quantity: z.number().int().nonnegative(),
    orders: z.number().int().nonnegative(),
    averageTicket: z.number(),
  }),
  filters: z
    .object({
      products: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
    })
    .optional(),
});
export type OrdersList = z.infer<typeof ordersListSchema>;

export const salesReportChannelSchema = z.enum(['TIENDANUBE', 'MERCADOLIBRE', 'MANUAL']);
export type SalesReportChannel = z.infer<typeof salesReportChannelSchema>;

export const salesReportQuerySchema = z
  .object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha desde debe tener formato YYYY-MM-DD.'),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha hasta debe tener formato YYYY-MM-DD.'),
    channel: salesReportChannelSchema.optional(),
    productId: z.string().uuid().optional(),
    categoryId: z.string().trim().max(191).optional(),
  })
  .strict();
export type SalesReportQuery = z.infer<typeof salesReportQuerySchema>;

const salesReportMetricSchema = z.object({
  amount: z.number(),
  quantity: z.number().int().nonnegative(),
  orders: z.number().int().nonnegative(),
});

const salesReportChargeSchema = z.object({
  amount: z.number(),
  orders: z.number().int().nonnegative(),
});

const salesReportFinancialSchema = z.object({
  saleCommissions: salesReportChargeSchema,
  paymentFees: salesReportChargeSchema,
  financingCosts: salesReportChargeSchema,
  estimatedReleaseFees: salesReportChargeSchema,
  taxes: salesReportChargeSchema,
  shippingCosts: salesReportChargeSchema,
  discounts: salesReportChargeSchema,
  netSales: z.number(),
});

const salesReportProductMetricSchema = salesReportMetricSchema.extend({
  productId: z.string().uuid().nullable(),
  productName: z.string(),
  sku: z.string().nullable(),
});

export const salesReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  summary: salesReportMetricSchema.extend({ averageTicket: z.number() }),
  financial: salesReportFinancialSchema,
  byDay: z.array(salesReportMetricSchema.extend({ key: z.string(), label: z.string() })),
  byMonth: z.array(salesReportMetricSchema.extend({ key: z.string(), label: z.string() })),
  bySize: z.array(salesReportMetricSchema.extend({ size: z.string() })),
  byProduct: z.array(salesReportProductMetricSchema),
  byCategory: z.array(
    salesReportMetricSchema.extend({
      categoryId: z.string(),
      categoryName: z.string(),
      products: z.array(salesReportProductMetricSchema),
    }),
  ),
  byChannel: z.array(
    salesReportMetricSchema.extend({
      channel: salesReportChannelSchema,
      label: z.string(),
      financial: salesReportFinancialSchema,
      orderDetails: z.array(
        z.object({
          id: z.string().uuid(),
          orderNumber: z.string(),
          orderedAt: z.string().datetime(),
          quantity: z.number().int().nonnegative(),
          amount: z.number(),
        }),
      ),
    }),
  ),
  filters: z.object({
    products: z.array(z.object({ id: z.string().uuid().nullable(), name: z.string() })),
    categories: z.array(z.object({ id: z.string(), name: z.string() })),
  }),
});
export type SalesReport = z.infer<typeof salesReportSchema>;

export const orderCustomerSummaryQuerySchema = z.object({ customerId: z.string().uuid() }).strict();
export type OrderCustomerSummaryQuery = z.infer<typeof orderCustomerSummaryQuerySchema>;
export const orderCustomerSummarySchema = z.object({
  totalOrders: z.number().int().nonnegative(),
  totalUnits: z.number().int().nonnegative(),
  totalAmount: z.number().nonnegative(),
});
export type OrderCustomerSummary = z.infer<typeof orderCustomerSummarySchema>;

export const orderHistoryQuerySchema = paginationQuerySchema
  .extend({ customerId: z.string().uuid() })
  .strict();
export type OrderHistoryQuery = z.infer<typeof orderHistoryQuerySchema>;
export const orderHistoryEntrySchema = z.object({
  id: z.string().uuid(),
  orderId: z.string().uuid(),
  orderNumber: z.string(),
  field: z.enum(['operationalStatus', 'billingStatus', 'platformStatus']),
  previousValue: z.string().nullable(),
  currentValue: z.string().nullable(),
  source: z.enum(['MANUAL', 'AUTOMATIC', 'PLATFORM']),
  changedAt: z.string().datetime(),
});
export type OrderHistoryEntry = z.infer<typeof orderHistoryEntrySchema>;
export const orderHistoryListSchema = paginatedSchema(orderHistoryEntrySchema);
export type OrderHistoryList = z.infer<typeof orderHistoryListSchema>;

export const orderSyncResponseSchema = z.object({
  jobIds: z.array(z.string().uuid()),
});
export type OrderSyncResponse = z.infer<typeof orderSyncResponseSchema>;

export const invoiceKindSchema = z.enum(['FISCAL', 'INTERNAL']);
export type InvoiceKind = z.infer<typeof invoiceKindSchema>;

export const invoiceVoucherTypeSchema = z.enum([
  'FACTURA_A',
  'FACTURA_B',
  'FACTURA_C',
  'FACTURA_X',
]);
export type InvoiceVoucherType = z.infer<typeof invoiceVoucherTypeSchema>;

export const invoiceStatusSchema = z.enum(['DRAFT', 'ISSUED', 'CANCELLED', 'ERROR']);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

export const invoicesQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().max(120).optional(),
  kind: invoiceKindSchema.optional(),
  voucherType: invoiceVoucherTypeSchema.optional(),
  status: z.union([invoiceStatusSchema, z.literal('PENDING')]).optional(),
  customerId: z.string().uuid().optional(),
  automation: z.literal('ARCA_AUTO').optional(),
  sourceChannel: channelSchema.optional(),
  from: orderDateQuerySchema.optional(),
  to: orderDateQuerySchema.optional(),
});
export type InvoicesQuery = z.infer<typeof invoicesQuerySchema>;

export const invoiceRowSchema = z.object({
  id: z.string().uuid(),
  orderId: z.string().uuid().nullable(),
  externalOrderId: z.string().nullable().optional(),
  customerId: z.string().uuid().nullable(),
  kind: invoiceKindSchema,
  documentType: z.enum(['INVOICE', 'CREDIT_NOTE', 'DEBIT_NOTE']),
  relatedInvoiceId: z.string().uuid().nullable(),
  voucherType: invoiceVoucherTypeSchema,
  status: invoiceStatusSchema,
  pointOfSale: z.string().nullable(),
  number: z.string().nullable(),
  customerName: z.string().nullable(),
  customerTaxId: z.string().nullable(),
  currency: z.string(),
  totalAmount: z.number(),
  issuedAt: z.string().datetime().nullable(),
  arcaStatus: z.enum(['NOT_APPLICABLE', 'PENDING', 'ISSUED', 'ERROR']),
  errorMessage: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type InvoiceRow = z.infer<typeof invoiceRowSchema>;

export const invoicesListSchema = paginatedSchema(invoiceRowSchema).extend({
  summary: z.object({
    amount: z.number(),
    invoices: z.number().int().nonnegative(),
  }),
});
export type InvoicesList = z.infer<typeof invoicesListSchema>;
export const invoiceCreateInputSchema = z
  .object({
    orderId: z.string().uuid().nullable().optional(),
    customerId: z.string().uuid().nullable().optional(),
    kind: invoiceKindSchema,
    documentType: z.enum(['INVOICE', 'CREDIT_NOTE', 'DEBIT_NOTE']).default('INVOICE'),
    relatedInvoiceId: z.string().uuid().nullable().optional(),
    voucherType: invoiceVoucherTypeSchema.optional(),
    pointOfSale: z.string().trim().max(10).nullable().optional(),
    customerName: z.string().trim().max(255).nullable().optional(),
    customerTaxId: z.string().trim().max(32).nullable().optional(),
    metadata: z
      .object({
        documentDate: z.string().date().nullable().optional(),
        customerContact: z.string().trim().max(255).nullable().optional(),
        customerEmail: z.string().trim().max(320).nullable().optional(),
        customerPhone: z.string().trim().max(80).nullable().optional(),
        customerTaxCondition: z.string().trim().max(80).nullable().optional(),
        customerAddress: z.string().trim().max(500).nullable().optional(),
        customerCity: z.string().trim().max(120).nullable().optional(),
        customerProvince: z.string().trim().max(120).nullable().optional(),
        customerPostalCode: z.string().trim().max(30).nullable().optional(),
        deliveryPlace: z.string().trim().max(500).nullable().optional(),
        notes: z.string().max(5000).nullable().optional(),
      })
      .strict()
      .nullable()
      .optional(),
    currency: z.string().trim().min(3).max(10).default('ARS'),
    lines: z
      .array(
        z.object({
          productVariantId: z.string().uuid().nullable().optional(),
          description: z.string().trim().min(2).max(500),
          quantity: z.coerce.number().int().positive(),
          unitPrice: z.coerce.number().min(0),
        }),
      )
      .min(1),
  })
  .strict();
export type InvoiceCreateInput = z.infer<typeof invoiceCreateInputSchema>;

// Factory / wholesale management
export const customerTypeSchema = z.enum(['RETAIL', 'WHOLESALE']);
export const customerSourceSchema = z.enum(['MANUAL', 'TIENDANUBE', 'MERCADOLIBRE']);
export const customerDeliveryLocationSchema = z.object({
  id: z.string().uuid(),
  customerId: z.string().uuid(),
  name: z.string(),
  address: z.string(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  postalCode: z.string().nullable(),
  contactName: z.string().nullable(),
  phone: z.string().nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type CustomerDeliveryLocation = z.infer<typeof customerDeliveryLocationSchema>;
export const customerDeliveryLocationInputSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().min(2).max(120),
  address: z.string().trim().min(2).max(500),
  city: z.string().trim().max(120).nullable().optional(),
  province: z.string().trim().max(120).nullable().optional(),
  postalCode: z.string().trim().max(30).nullable().optional(),
  contactName: z.string().trim().max(255).nullable().optional(),
  phone: z.string().trim().max(80).nullable().optional(),
  isDefault: z.boolean().default(false),
  isActive: z.boolean().default(true),
});
export type CustomerDeliveryLocationInput = z.infer<typeof customerDeliveryLocationInputSchema>;
export const customerSchema = z.object({
  id: z.string().uuid(),
  type: customerTypeSchema,
  source: customerSourceSchema,
  sellerId: z.string().uuid().nullable(),
  sellerName: z.string().nullable(),
  businessName: z.string(),
  contactName: z.string().nullable(),
  taxId: z.string().nullable(),
  taxCondition: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  postalCode: z.string().nullable(),
  deliveryLocations: z.array(customerDeliveryLocationSchema),
  paymentTermsDays: z.number().int().nonnegative(),
  creditLimit: z.number().nonnegative(),
  discountPercent: z.number().nonnegative().max(100),
  notes: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Customer = z.infer<typeof customerSchema>;
export const customersExportQuerySchema = z
  .object({
    search: z.string().trim().max(120).optional(),
    type: customerTypeSchema.optional(),
    source: customerSourceSchema.optional(),
  })
  .strict();
export type CustomersExportQuery = z.infer<typeof customersExportQuerySchema>;
export const customerInputSchema = z
  .object({
    type: customerTypeSchema.default('WHOLESALE'),
    sellerId: z.string().uuid().nullable().optional(),
    businessName: z.string().trim().min(2).max(255),
    contactName: z.string().trim().max(255).nullable().optional(),
    taxId: z.string().trim().max(32).nullable().optional(),
    taxCondition: z.string().trim().max(80).nullable().optional(),
    email: z.string().trim().email().max(320).nullable().optional(),
    phone: z.string().trim().max(80).nullable().optional(),
    address: z.string().trim().max(500).nullable().optional(),
    city: z.string().trim().max(120).nullable().optional(),
    province: z.string().trim().max(120).nullable().optional(),
    postalCode: z.string().trim().max(30).nullable().optional(),
    deliveryLocations: z.array(customerDeliveryLocationInputSchema).default([]),
    paymentTermsDays: z.coerce.number().int().min(0).max(365).default(0),
    creditLimit: z.coerce.number().min(0).default(0),
    discountPercent: z.coerce.number().min(0).max(100).default(0),
    notes: z.string().max(5000).nullable().optional(),
    isActive: z.boolean().default(true),
  })
  .strict();
export type CustomerInput = z.infer<typeof customerInputSchema>;
export const customersListSchema = z.array(customerSchema);

export const sellerSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  commissionPercent: z.number().nonnegative(),
  notes: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export const sellerInputSchema = z
  .object({
    name: z.string().trim().min(2).max(255),
    email: z.string().trim().email().max(320).nullable().optional(),
    phone: z.string().trim().max(80).nullable().optional(),
    commissionPercent: z.coerce.number().min(0).max(100).default(0),
    notes: z.string().max(5000).nullable().optional(),
    isActive: z.boolean().default(true),
  })
  .strict();
export type Seller = z.infer<typeof sellerSchema>;
export type SellerInput = z.infer<typeof sellerInputSchema>;
export const sellersListSchema = z.array(sellerSchema);

export const supplierKindSchema = z.enum(['SUPPLIER', 'EXTERNAL_WORKSHOP', 'BOTH']);
export const supplierSchema = z.object({
  id: z.string().uuid(),
  kind: supplierKindSchema,
  businessName: z.string(),
  contactName: z.string().nullable(),
  taxId: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  notes: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export const supplierInputSchema = z
  .object({
    kind: supplierKindSchema.default('SUPPLIER'),
    businessName: z.string().trim().min(2).max(255),
    contactName: z.string().trim().max(255).nullable().optional(),
    taxId: z.string().trim().max(32).nullable().optional(),
    email: z.string().trim().email().max(320).nullable().optional(),
    phone: z.string().trim().max(80).nullable().optional(),
    address: z.string().trim().max(500).nullable().optional(),
    notes: z.string().max(5000).nullable().optional(),
    isActive: z.boolean().default(true),
  })
  .strict();
export type Supplier = z.infer<typeof supplierSchema>;
export type SupplierInput = z.infer<typeof supplierInputSchema>;
export const suppliersListSchema = z.array(supplierSchema);

export const rawMaterialSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  sku: z.string().nullable(),
  unit: z.string(),
  unitCost: z.number().nonnegative(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export const rawMaterialInputSchema = z
  .object({
    name: z.string().trim().min(2).max(255),
    sku: z.string().trim().max(120).nullable().optional(),
    unit: z.string().trim().min(1).max(20).default('UN'),
    unitCost: z.coerce.number().min(0),
    isActive: z.boolean().default(true),
  })
  .strict();
export type RawMaterial = z.infer<typeof rawMaterialSchema>;
export type RawMaterialInput = z.infer<typeof rawMaterialInputSchema>;
export const rawMaterialsListSchema = z.array(rawMaterialSchema);
export const productCompositionSchema = z.object({
  productId: z.string().uuid(),
  materials: z.array(
    z.object({
      id: z.string().uuid(),
      rawMaterialId: z.string().uuid(),
      name: z.string(),
      unit: z.string(),
      quantity: z.number().positive(),
      unitCost: z.number().nonnegative(),
      totalCost: z.number().nonnegative(),
    }),
  ),
  otherCosts: z.array(
    z.object({
      id: z.string().uuid(),
      description: z.string(),
      category: z.enum(['DIRECTO', 'FINANCIERO', 'COMISION', 'LOGISTICA', 'OTRO']),
      quantity: z.number().positive(),
      amount: z.number().nonnegative(),
    }),
  ),
  processes: z.array(
    z.object({
      id: z.string().uuid(),
      processId: z.string().uuid(),
      processStepId: z.string().uuid().nullable(),
      supplierId: z.string().uuid().nullable(),
      supplierName: z.string().nullable(),
      processName: z.string(),
      processStepName: z.string().nullable(),
      quantity: z.number().positive(),
      unitCost: z.number().nonnegative(),
      sortOrder: z.number().int().nonnegative(),
    }),
  ),
  totalCost: z.number().nonnegative(),
});
export type ProductComposition = z.infer<typeof productCompositionSchema>;
export const compositionMaterialInputSchema = z.object({
  rawMaterialId: z.string().uuid(),
  quantity: z.coerce.number().positive(),
});
export const compositionOtherCostInputSchema = z.object({
  description: z.string().trim().min(2).max(255),
  category: z.enum(['DIRECTO', 'FINANCIERO', 'COMISION', 'LOGISTICA', 'OTRO']).default('OTRO'),
  quantity: z.coerce.number().positive().default(1),
  amount: z.coerce.number().min(0),
});
export const compositionProcessInputSchema = z.object({
  processId: z.string().uuid(),
  processStepId: z.string().uuid().nullable().optional(),
  supplierId: z.string().uuid().nullable().optional(),
  quantity: z.coerce.number().positive().default(1),
  unitCost: z.coerce.number().min(0).default(0),
});

export const collectionSchema = z.object({
  id: z.string().uuid(),
  customerId: z.string().uuid(),
  invoiceId: z.string().uuid().nullable(),
  orderId: z.string().uuid().nullable(),
  source: z.enum(['MANUAL', 'TIENDANUBE', 'MERCADOLIBRE']),
  externalOrderId: z.string().nullable(),
  externalPaymentId: z.string().nullable(),
  customerName: z.string(),
  amount: z.number(),
  currency: z.string(),
  method: z.enum(['CASH', 'TRANSFER', 'CARD', 'CHECK', 'MERCADOPAGO', 'OTHER']),
  reference: z.string().nullable(),
  notes: z.string().nullable(),
  collectedAt: z.string().datetime(),
  status: z.enum(['PENDING', 'COLLECTED', 'CANCELLED']),
});
export const collectionInputSchema = z
  .object({
    customerId: z.string().uuid(),
    invoiceId: z.string().uuid().nullable().optional(),
    amount: z.coerce.number().positive(),
    method: z.enum(['CASH', 'TRANSFER', 'CARD', 'CHECK', 'MERCADOPAGO', 'OTHER']),
    reference: z.string().trim().max(191).nullable().optional(),
    notes: z.string().max(5000).nullable().optional(),
    collectedAt: z.coerce.date().optional(),
  })
  .strict();
export type Collection = z.infer<typeof collectionSchema>;
export type CollectionInput = z.infer<typeof collectionInputSchema>;
export const collectionsListSchema = z.array(collectionSchema);

export const checkTypeSchema = z.enum(['RECEIVED', 'ISSUED_OWN', 'DELIVERED_THIRD_PARTY']);
export const checkStatusSchema = z.enum([
  'IN_CUSTODY',
  'DEPOSITED',
  'CASHED',
  'ISSUED',
  'DELIVERED',
  'REJECTED',
  'CANCELLED',
]);
export const checkbookSchema = z.object({
  id: z.string().uuid(),
  bank: z.string(),
  branch: z.string().nullable(),
  accountNumber: z.string(),
  numberFrom: z.number().int().positive(),
  numberTo: z.number().int().positive(),
  nextNumber: z.number().int().positive(),
  isActive: z.boolean(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Checkbook = z.infer<typeof checkbookSchema>;
export const checkbookInputSchema = z
  .object({
    bank: z.string().trim().min(1).max(191),
    branch: z.string().trim().max(80).nullable().optional(),
    accountNumber: z.string().trim().min(1).max(80),
    numberFrom: z.coerce.number().int().positive(),
    numberTo: z.coerce.number().int().positive(),
    isActive: z.boolean().default(true),
    notes: z.string().max(5000).nullable().optional(),
  })
  .refine((value) => value.numberTo >= value.numberFrom, {
    message: 'El número final debe ser mayor o igual al inicial',
    path: ['numberTo'],
  })
  .strict();
export type CheckbookInput = z.infer<typeof checkbookInputSchema>;
export const checkbooksListSchema = z.array(checkbookSchema);
export const checkSchema = z.object({
  id: z.string().uuid(),
  type: checkTypeSchema,
  status: checkStatusSchema,
  checkbookId: z.string().uuid().nullable(),
  checkbookLabel: z.string().nullable(),
  number: z.string(),
  bank: z.string(),
  branch: z.string().nullable(),
  accountNumber: z.string().nullable(),
  issuerName: z.string().nullable(),
  counterpartyName: z.string(),
  amount: z.number().positive(),
  issuedAt: z.string().datetime(),
  dueDate: z.string().datetime(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Check = z.infer<typeof checkSchema>;
export const checkInputSchema = z
  .object({
    type: checkTypeSchema,
    status: checkStatusSchema.optional(),
    checkbookId: z.string().uuid().nullable().optional(),
    number: z.string().trim().min(1).max(60),
    bank: z.string().trim().min(1).max(191),
    branch: z.string().trim().max(80).nullable().optional(),
    accountNumber: z.string().trim().max(80).nullable().optional(),
    issuerName: z.string().trim().max(255).nullable().optional(),
    counterpartyName: z.string().trim().min(1).max(255),
    amount: z.coerce.number().positive(),
    issuedAt: z.coerce.date().optional(),
    dueDate: z.coerce.date(),
    notes: z.string().max(5000).nullable().optional(),
  })
  .strict();
export type CheckInput = z.infer<typeof checkInputSchema>;
export const checksListSchema = z.array(checkSchema);

export const simpleBusinessRowSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  subtitle: z.string().nullable(),
  amount: z.number().nullable(),
  status: z.string(),
  date: z.string().datetime(),
});
export const simpleBusinessListSchema = z.array(simpleBusinessRowSchema);
export type SimpleBusinessRow = z.infer<typeof simpleBusinessRowSchema>;

export const purchaseSchema = z.object({
  id: z.string().uuid(),
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  voucherType: z.enum(['FACTURA_A', 'FACTURA_B', 'FACTURA_C', 'FACTURA_X']),
  documentType: z.enum(['INVOICE', 'CREDIT_NOTE', 'DEBIT_NOTE']),
  relatedPurchaseId: z.string().uuid().nullable(),
  number: z.string().nullable(),
  totalAmount: z.number().nonnegative(),
  purchasedAt: z.string().datetime(),
  notes: z.string().nullable(),
  status: z.enum(['PENDING', 'RECEIVED', 'CANCELLED']),
  paymentStatus: z.enum(['PENDING', 'PARTIAL', 'PAID', 'CANCELLED']),
});
export type Purchase = z.infer<typeof purchaseSchema>;
export const purchasesListSchema = z.array(purchaseSchema);
export const purchaseLinePricingModeSchema = z.enum(['PER_PROCESS', 'TOTAL']);
export type PurchaseLinePricingMode = z.infer<typeof purchaseLinePricingModeSchema>;
export const purchaseLineSchema = z.object({
  id: z.string().uuid(),
  lineKind: z.enum(['PRODUCT', 'SERVICE']),
  productVariantId: z.string().uuid().nullable(),
  workshopOrderId: z.string().uuid().nullable(),
  productProcessId: z.string().uuid().nullable(),
  inventoryLocationId: z.string().uuid().nullable(),
  pricingMode: purchaseLinePricingModeSchema,
  description: z.string(),
  quantity: z.number().int().positive(),
  unitCost: z.number().nonnegative(),
  lineTotal: z.number().nonnegative(),
});
export const purchaseDetailSchema = purchaseSchema.extend({
  lines: z.array(purchaseLineSchema),
});
export type PurchaseDetail = z.infer<typeof purchaseDetailSchema>;

const paymentMethodSchema = z.enum(['CASH', 'TRANSFER', 'CARD', 'CHECK', 'OTHER']);
const paymentMethodLineSchema = z.object({
  method: paymentMethodSchema,
  amount: z.number().positive(),
  reference: z.string().nullable(),
  documentNumber: z.string().nullable(),
  checkNumber: z.string().nullable(),
  dueDate: z.string().datetime().nullable(),
  bank: z.string().nullable(),
  bankAccountId: z.string().uuid().nullable(),
  notes: z.string().nullable(),
});
export const paymentSchema = z.object({
  id: z.string().uuid(),
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  purchaseId: z.string().uuid().nullable(),
  allocations: z.array(
    z.object({
      purchaseId: z.string().uuid(),
      amount: z.number().positive(),
    }),
  ),
  methods: z.array(paymentMethodLineSchema),
  amount: z.number().positive(),
  method: paymentMethodSchema,
  reference: z.string().nullable(),
  paidAt: z.string().datetime(),
  notes: z.string().nullable(),
  status: z.enum(['PENDING', 'PAID', 'CANCELLED']),
});
export type Payment = z.infer<typeof paymentSchema>;
export const paymentsListSchema = z.array(paymentSchema);

export const expenseSchema = z.object({
  id: z.string().uuid(),
  categoryId: z.string().uuid(),
  categoryName: z.string(),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  description: z.string(),
  amount: z.number().positive(),
  expenseDate: z.string().datetime(),
  paymentMethod: z.enum(['CASH', 'TRANSFER', 'CARD', 'CHECK', 'OTHER']).nullable(),
  notes: z.string().nullable(),
});
export type Expense = z.infer<typeof expenseSchema>;
export const expensesListSchema = z.array(expenseSchema);

export const expenseCategorySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean(),
});
export const expenseCategoryInputSchema = z
  .object({ name: z.string().trim().min(2).max(120), isActive: z.boolean().default(true) })
  .strict();
export const expenseCategoriesListSchema = z.array(expenseCategorySchema);

export const operationalStatusSchema = z.enum([
  'PENDING',
  'PREPARING',
  'FINISHED',
  'DISPATCHED',
  'DELIVERED',
  'CANCELLED',
]);
export const billingStatusSchema = z.enum([
  'PENDING',
  'PARTIAL',
  'COMPLETE',
  'INCOMPLETE',
  'CANCELLED',
]);
export const createManualOrderSchema = z
  .object({
    customerId: z.string().uuid(),
    sellerId: z.string().uuid().nullable().optional(),
    notes: z.string().max(5000).nullable().optional(),
    discountPercent: z.coerce.number().min(0).max(100).optional(),
    lines: z
      .array(
        z.object({
          productVariantId: z.string().uuid(),
          quantity: z.coerce.number().int().positive(),
          unitPrice: z.coerce.number().min(0),
          discountPercent: z.coerce.number().min(0).max(100).default(0),
        }),
      )
      .min(1),
  })
  .strict();
export type CreateManualOrder = z.infer<typeof createManualOrderSchema>;
export const updateOrderWorkflowSchema = z
  .object({
    operationalStatus: operationalStatusSchema.optional(),
    billingStatus: billingStatusSchema.optional(),
  })
  .strict();

export const businessOverviewSchema = z.object({
  customers: z.number().int().nonnegative(),
  wholesaleCustomers: z.number().int().nonnegative(),
  pendingOrders: z.number().int().nonnegative(),
  preparingOrders: z.number().int().nonnegative(),
  unbilledOrders: z.number().int().nonnegative(),
  receivables: z.number().nonnegative(),
  salesToday: z.number().nonnegative(),
  salesMonth: z.number().nonnegative(),
  invoicedMonth: z.number().nonnegative(),
  collectedMonth: z.number().nonnegative(),
  expensesMonth: z.number().nonnegative(),
  purchasesMonth: z.number().nonnegative(),
  wholesaleStock: z.number().int(),
  lowStockVariants: z.number().int().nonnegative(),
});

export const purchaseInputSchema = z
  .object({
    supplierId: z.string().uuid(),
    voucherType: z.enum(['FACTURA_A', 'FACTURA_B', 'FACTURA_C', 'FACTURA_X']),
    documentType: z.enum(['INVOICE', 'CREDIT_NOTE', 'DEBIT_NOTE']).default('INVOICE'),
    relatedPurchaseId: z.string().uuid().nullable().optional(),
    number: z.string().trim().max(60).nullable().optional(),
    totalAmount: z.coerce.number().min(0),
    purchasedAt: z.coerce.date().optional(),
    notes: z.string().max(5000).nullable().optional(),
    lines: z
      .array(
        z.object({
          lineKind: z.enum(['PRODUCT', 'SERVICE']).default('PRODUCT'),
          productVariantId: z.string().uuid().nullable().optional(),
          workshopOrderId: z.string().uuid().nullable().optional(),
          productProcessId: z.string().uuid().nullable().optional(),
          inventoryLocationId: z.string().uuid().nullable().optional(),
          pricingMode: purchaseLinePricingModeSchema.default('PER_PROCESS'),
          description: z.string().trim().min(2).max(500),
          quantity: z.coerce.number().int().positive(),
          unitCost: z.coerce.number().min(0),
        }),
      )
      .default([]),
  })
  .strict();
export type PurchaseInput = z.infer<typeof purchaseInputSchema>;
export const purchaseCreateInputSchema = purchaseInputSchema.extend({
  number: z.string().trim().min(1, 'El número del comprobante es obligatorio').max(60),
});
export type PurchaseCreateInput = z.infer<typeof purchaseCreateInputSchema>;

export const workshopProcessPriceSchema = z.object({
  id: z.string().uuid(),
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  processId: z.string().uuid(),
  processStepId: z.string().uuid().nullable(),
  processName: z.string(),
  processStepName: z.string().nullable(),
  unitCost: z.number().nonnegative(),
  currency: z.string(),
  isActive: z.boolean(),
  effectiveAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export const workshopProcessPriceInputSchema = z
  .object({
    supplierId: z.string().uuid(),
    processId: z.string().uuid(),
    processStepId: z.string().uuid().nullable().optional(),
    unitCost: z.coerce.number().min(0),
    currency: z.string().trim().min(1).max(10).default('ARS'),
    effectiveAt: z.coerce.date().optional(),
  })
  .strict();
export const workshopProcessPricesListSchema = z.array(workshopProcessPriceSchema);
export type WorkshopProcessPrice = z.infer<typeof workshopProcessPriceSchema>;
export type WorkshopProcessPriceInput = z.infer<typeof workshopProcessPriceInputSchema>;

export const productProcessReferenceSchema = z.object({
  id: z.string().uuid(),
  productId: z.string().uuid(),
  productName: z.string(),
  processId: z.string().uuid(),
  processStepId: z.string().uuid().nullable(),
  processName: z.string(),
  processStepName: z.string().nullable(),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  unitCost: z.number().nonnegative(),
});
export const productProcessReferencesListSchema = z.array(productProcessReferenceSchema);
export type ProductProcessReference = z.infer<typeof productProcessReferenceSchema>;

export const paymentInputSchema = z
  .object({
    supplierId: z.string().uuid(),
    purchaseId: z.string().uuid().nullable().optional(),
    allocations: z
      .array(
        z.object({
          purchaseId: z.string().uuid(),
          amount: z.coerce.number().positive(),
        }),
      )
      .optional(),
    methods: z
      .array(
        z.object({
          method: paymentMethodSchema,
          amount: z.coerce.number().positive(),
          reference: z.string().trim().max(191).nullable().optional(),
          documentNumber: z.string().trim().max(60).nullable().optional(),
          checkNumber: z.string().trim().max(60).nullable().optional(),
          dueDate: z.coerce.date().nullable().optional(),
          bank: z.string().trim().max(191).nullable().optional(),
          bankAccountId: z.string().uuid().nullable().optional(),
          notes: z.string().trim().max(5000).nullable().optional(),
        }),
      )
      .optional(),
    amount: z.coerce.number().positive(),
    method: paymentMethodSchema,
    reference: z.string().trim().max(191).nullable().optional(),
    paidAt: z.coerce.date().optional(),
    notes: z.string().max(5000).nullable().optional(),
  })
  .strict();
export type PaymentInput = z.infer<typeof paymentInputSchema>;

export const bankAccountTypeSchema = z.enum(['CHECKING', 'SAVINGS', 'OTHER']);
export const bankAccountSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  bankName: z.string(),
  accountType: bankAccountTypeSchema,
  accountNumber: z.string().nullable(),
  cbu: z.string().nullable(),
  alias: z.string().nullable(),
  openingBalance: z.number(),
  balance: z.number(),
  isActive: z.boolean(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type BankAccount = z.infer<typeof bankAccountSchema>;
export const bankAccountsListSchema = z.array(bankAccountSchema);
export const bankAccountInputSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    bankName: z.string().trim().min(2).max(191),
    accountType: bankAccountTypeSchema.default('CHECKING'),
    accountNumber: z.string().trim().max(80).nullable().optional(),
    cbu: z.string().trim().max(80).nullable().optional(),
    alias: z.string().trim().max(80).nullable().optional(),
    openingBalance: z.coerce.number().default(0),
    isActive: z.boolean().default(true),
    notes: z.string().max(5000).nullable().optional(),
  })
  .strict();
export type BankAccountInput = z.infer<typeof bankAccountInputSchema>;

export const bankMovementTypeSchema = z.enum([
  'TRANSFER_IN',
  'TRANSFER_OUT',
  'CHECK_DEPOSIT',
  'CHECK_WITHDRAWAL',
  'ADJUSTMENT',
]);
export const bankMovementSchema = z.object({
  id: z.string().uuid(),
  bankAccountId: z.string().uuid(),
  bankAccountName: z.string(),
  type: bankMovementTypeSchema,
  direction: z.enum(['IN', 'OUT']),
  amount: z.number().positive(),
  description: z.string(),
  reference: z.string().nullable(),
  checkId: z.string().uuid().nullable(),
  checkNumber: z.string().nullable(),
  sourceType: z.enum(['MANUAL', 'PAYMENT', 'COLLECTION', 'CHECK_DEPOSIT']),
  sourceId: z.string().uuid().nullable(),
  status: z.enum(['POSTED', 'VOIDED']),
  movementDate: z.string().datetime(),
  createdAt: z.string().datetime(),
});
export type BankMovement = z.infer<typeof bankMovementSchema>;
export const bankMovementsListSchema = z.array(bankMovementSchema);
export const bankMovementInputSchema = z
  .object({
    bankAccountId: z.string().uuid(),
    type: bankMovementTypeSchema,
    amount: z.coerce.number().positive(),
    description: z.string().trim().min(2).max(255),
    reference: z.string().trim().max(191).nullable().optional(),
    checkId: z.string().uuid().nullable().optional(),
    direction: z.enum(['IN', 'OUT']).optional(),
    movementDate: z.coerce.date().optional(),
  })
  .strict();
export type BankMovementInput = z.infer<typeof bankMovementInputSchema>;

export const expenseInputSchema = z
  .object({
    categoryId: z.string().uuid(),
    supplierId: z.string().uuid().nullable().optional(),
    description: z.string().trim().min(2).max(500),
    amount: z.coerce.number().positive(),
    expenseDate: z.coerce.date().optional(),
    paymentMethod: z.enum(['CASH', 'TRANSFER', 'CARD', 'CHECK', 'OTHER']).nullable().optional(),
    notes: z.string().max(5000).nullable().optional(),
  })
  .strict();

export const stockTransferInputSchema = z
  .object({
    productVariantId: z.string().uuid(),
    fromLocationId: z.string().uuid(),
    toLocationId: z.string().uuid(),
    quantity: z.coerce.number().int().positive(),
    reason: z.string().trim().min(2).max(500).default('Traspaso de stock'),
  })
  .strict();
export type StockTransferInput = z.infer<typeof stockTransferInputSchema>;
export const stockTransferResultSchema = z.object({
  transferId: z.string().uuid(),
  productVariantId: z.string().uuid(),
  quantity: z.number().int().positive(),
  fromLocationId: z.string().uuid(),
  toLocationId: z.string().uuid(),
});

export const workshopProcessStageSchema = z.enum([
  'DESIGN',
  'CUTTING',
  'STITCHING',
  'ASSEMBLY',
  'SOLE',
  'FINISHING',
  'PACKAGING',
]);
export type WorkshopStage = z.infer<typeof workshopProcessStageSchema>;
export const productionStageSchema = z.enum([...workshopProcessStageSchema.options, 'FINISHED']);
export type ProductionStage = z.infer<typeof productionStageSchema>;
export const productionOrderInputSchema = z
  .object({
    notes: z.string().max(5000).nullable().optional(),
    reportedAt: z.coerce.date().optional(),
    employeeId: z.string().uuid().nullable().optional(),
    lines: z
      .array(
        z.object({
          productVariantId: z.string().uuid(),
          plannedQuantity: z.coerce.number().int().positive(),
          processId: z.string().uuid().nullable().optional(),
          processStepId: z.string().uuid().nullable().optional(),
          inventoryLocationId: z.string().uuid().nullable().optional(),
        }),
      )
      .min(1),
  })
  .strict();
export const productionReportInputSchema = z
  .object({
    productionOrderLineId: z.string().uuid(),
    stage: productionStageSchema,
    processId: z.string().uuid().nullable().optional(),
    processStepId: z.string().uuid().nullable().optional(),
    quantity: z.coerce.number().int().positive(),
    supplierId: z.string().uuid().nullable().optional(),
    inventoryLocationId: z.string().uuid().nullable().optional(),
    notes: z.string().max(5000).nullable().optional(),
    reportedAt: z.coerce.date().optional(),
    employeeId: z.string().uuid().nullable().optional(),
  })
  .strict();
export const workshopOrderInputSchema = z
  .object({
    productionOrderLineId: z.string().uuid(),
    supplierId: z.string().uuid(),
    processId: z.string().uuid().nullable().optional(),
    processStepId: z.string().uuid().nullable().optional(),
    stage: workshopProcessStageSchema,
    quantitySent: z.coerce.number().int().positive(),
    unitCost: z.coerce.number().min(0).nullable().optional(),
    notes: z.string().max(5000).nullable().optional(),
  })
  .strict();
export const workshopOrderReceiptInputSchema = z
  .object({
    quantity: z.coerce.number().int().nonnegative(),
    waste: z.coerce.number().int().nonnegative().default(0),
    wasteReason: z.string().trim().max(500).nullable().optional(),
  })
  .superRefine((value, context) => {
    if (value.quantity === 0 && value.waste === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['quantity'],
        message: 'Informá unidades recibidas o merma',
      });
    }
  })
  .strict();
export const workshopReceiptItemInputSchema = z
  .object({
    workshopOrderId: z.string().uuid(),
    quantity: z.coerce.number().int().nonnegative(),
    waste: z.coerce.number().int().nonnegative().default(0),
    wasteReason: z.string().trim().max(500).nullable().optional(),
  })
  .superRefine((value, context) => {
    if (value.quantity === 0 && value.waste === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['quantity'],
        message: 'Informá unidades recibidas o merma',
      });
    }
  })
  .strict();
export const workshopReceiptInputSchema = z
  .object({
    supplierId: z.string().uuid(),
    receivedAt: z.coerce.date(),
    notes: z.string().trim().max(5000).nullable().optional(),
    items: z.array(workshopReceiptItemInputSchema).min(1),
  })
  .superRefine((value, context) => {
    const ids = new Set<string>();
    value.items.forEach((item, index) => {
      if (ids.has(item.workshopOrderId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items', index, 'workshopOrderId'],
          message: 'La orden de taller no puede repetirse',
        });
      }
      ids.add(item.workshopOrderId);
    });
  })
  .strict();
export const productionOrderSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  status: z.enum(['PLANNED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED']),
  notes: z.string().nullable(),
  startedAt: z.string().datetime().nullable(),
  reportedAt: z.string().datetime(),
  employeeId: z.string().uuid().nullable(),
  employeeName: z.string().nullable(),
  completedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  lines: z.array(
    z.object({
      id: z.string().uuid(),
      productVariantId: z.string().uuid(),
      productId: z.string().uuid(),
      sku: z.string().nullable(),
      productName: z.string(),
      variantName: z.string().nullable(),
      imageUrl: z.string().url().nullable(),
      plannedQuantity: z.number().int(),
      completedQuantity: z.number().int(),
      wastedQuantity: z.number().int().nonnegative(),
      stages: z.record(z.string(), z.number().int().nonnegative()),
      stageWaste: z.record(z.string(), z.number().int().nonnegative()),
      processes: z.array(
        z.object({
          processId: z.string().uuid().nullable(),
          processStepId: z.string().uuid().nullable(),
          processName: z.string().nullable(),
          processStepName: z.string().nullable(),
          stage: productionStageSchema,
          quantity: z.number().int().nonnegative(),
        }),
      ),
    }),
  ),
});
export const productionOrdersListSchema = z.array(productionOrderSchema);
export const workshopOrderSchema = z.object({
  id: z.string().uuid(),
  productionOrderLineId: z.string().uuid(),
  productVariantId: z.string().uuid(),
  productId: z.string().uuid(),
  sku: z.string().nullable(),
  productName: z.string(),
  variantName: z.string().nullable(),
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  processId: z.string().uuid().nullable(),
  processStepId: z.string().uuid().nullable(),
  processName: z.string().nullable(),
  processStepName: z.string().nullable(),
  stage: workshopProcessStageSchema,
  quantitySent: z.number().int(),
  quantityReceived: z.number().int(),
  quantityWaste: z.number().int().nonnegative(),
  quantityPending: z.number().int().nonnegative(),
  wasteReason: z.string().nullable(),
  quantityInvoiced: z.number().int().nonnegative(),
  quantityPendingInvoice: z.number().int().nonnegative(),
  unitCost: z.number().nonnegative().nullable(),
  status: z.enum(['PENDING', 'SENT', 'PARTIAL', 'RECEIVED', 'CANCELLED']),
  sentAt: z.string().datetime().nullable(),
  receivedAt: z.string().datetime().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export const workshopOrdersListSchema = z.array(workshopOrderSchema);
export const workshopReceiptItemSchema = z.object({
  id: z.string().uuid(),
  workshopOrderId: z.string().uuid(),
  productVariantId: z.string().uuid(),
  productName: z.string(),
  variantName: z.string().nullable(),
  sku: z.string().nullable(),
  processName: z.string().nullable(),
  processStepName: z.string().nullable(),
  quantity: z.number().int().nonnegative(),
  waste: z.number().int().nonnegative(),
  quantityInvoiced: z.number().int().nonnegative(),
  wasteReason: z.string().nullable(),
});
export const workshopReceiptSchema = z.object({
  id: z.string().uuid(),
  number: z.number().int().positive(),
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  receivedAt: z.string().datetime(),
  notes: z.string().nullable(),
  totalQuantity: z.number().int().nonnegative(),
  totalWaste: z.number().int().nonnegative(),
  canModify: z.boolean(),
  createdAt: z.string().datetime(),
  items: z.array(workshopReceiptItemSchema),
});
export const workshopReceiptsListSchema = z.array(workshopReceiptSchema);
export const workshopShipmentItemInputSchema = z
  .object({
    productionOrderLineId: z.string().uuid(),
    processId: z.string().uuid().nullable().optional(),
    processStepId: z.string().uuid().nullable().optional(),
    stage: workshopProcessStageSchema,
    quantitySent: z.coerce.number().int().positive(),
    unitCost: z.coerce.number().min(0).nullable().optional(),
  })
  .strict();
export const workshopShipmentInputSchema = z
  .object({
    supplierId: z.string().uuid(),
    sentAt: z.coerce.date(),
    notes: z.string().trim().max(5000).nullable().optional(),
    items: z.array(workshopShipmentItemInputSchema).min(1),
  })
  .strict();
export const workshopShipmentItemSchema = z.object({
  id: z.string().uuid(),
  workshopOrderId: z.string().uuid(),
  productionOrderLineId: z.string().uuid(),
  productVariantId: z.string().uuid(),
  productName: z.string(),
  variantName: z.string().nullable(),
  sku: z.string().nullable(),
  processId: z.string().uuid().nullable(),
  processStepId: z.string().uuid().nullable(),
  processName: z.string().nullable(),
  processStepName: z.string().nullable(),
  stage: workshopProcessStageSchema,
  quantitySent: z.number().int(),
  quantityReceived: z.number().int(),
  quantityWaste: z.number().int().nonnegative(),
  quantityPending: z.number().int().nonnegative(),
  status: z.enum(['PENDING', 'SENT', 'PARTIAL', 'RECEIVED', 'CANCELLED']),
  unitCost: z.number().nonnegative().nullable(),
  notes: z.string().nullable(),
});
export const workshopShipmentSchema = z.object({
  id: z.string().uuid(),
  number: z.number().int().positive(),
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  sentAt: z.string().datetime(),
  notes: z.string().nullable(),
  totalSent: z.number().int().nonnegative(),
  totalReceived: z.number().int().nonnegative(),
  totalWaste: z.number().int().nonnegative(),
  totalPending: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  items: z.array(workshopShipmentItemSchema),
});
export const workshopShipmentsListSchema = z.array(workshopShipmentSchema);

export const workshopProcessStepSchema = z.object({
  id: z.string().uuid(),
  processId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  sortOrder: z.number().int().nonnegative(),
  isActive: z.boolean(),
  workshopIds: z.array(z.string().uuid()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export const workshopProcessStepInputSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().min(2).max(160),
  description: z.string().trim().max(5000).nullable().optional(),
  sortOrder: z.coerce.number().int().nonnegative().default(0),
  isActive: z.boolean().default(true),
  workshopIds: z.array(z.string().uuid()).default([]),
});
export type WorkshopProcessStep = z.infer<typeof workshopProcessStepSchema>;
export type WorkshopProcessStepInput = z.infer<typeof workshopProcessStepInputSchema>;
export const workshopProcessSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  stage: workshopProcessStageSchema,
  isActive: z.boolean(),
  workshopIds: z.array(z.string().uuid()),
  steps: z.array(workshopProcessStepSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export const workshopProcessInputSchema = z
  .object({
    name: z.string().trim().min(2).max(160),
    description: z.string().trim().max(5000).nullable().optional(),
    stage: workshopProcessStageSchema.default('STITCHING'),
    isActive: z.boolean().default(true),
    workshopIds: z.array(z.string().uuid()).default([]),
    steps: z.array(workshopProcessStepInputSchema).default([]),
  })
  .strict();
export const workshopProcessesListSchema = z.array(workshopProcessSchema);
export type WorkshopProcess = z.infer<typeof workshopProcessSchema>;
export type WorkshopProcessInput = z.infer<typeof workshopProcessInputSchema>;

export const inventoryReferenceDataSchema = z.object({
  variants: z.array(
    z.object({
      id: z.string().uuid(),
      productId: z.string().uuid(),
      sku: z.string().nullable(),
      productName: z.string(),
      variantName: z.string().nullable(),
    }),
  ),
  locations: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      purpose: z.enum(['GENERAL', 'WHOLESALE', 'RESERVE']),
      includedInAvailable: z.boolean(),
    }),
  ),
});
